import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useGeminiApi } from "@/hooks/useGeminiApi";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Document } from "./DocumentTable";
import { supabase } from "@/integrations/supabase/client";
import { prepareDocumentForAI, isAnalysisSupported } from "@/utils/documentParser";
import { useLanguage } from "@/contexts/LanguageContext";

interface MultiDocumentAnalysisProps {
  documents: Document[];
  tenderId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const MultiDocumentAnalysis = ({
  documents,
  tenderId,
  isOpen,
  onClose
}: MultiDocumentAnalysisProps) => {
  const [analysisResults, setAnalysisResults] = useState<string | null>(null);
  const { generateContent, isLoading } = useGeminiApi();
  const { toast } = useToast();
  const { t } = useTranslation('offerManagement');
  const { currentLanguage } = useLanguage();

  const analyzeDocuments = async () => {
    // Pre-check if there are any supported documents
    const supportedDocs = documents.filter(doc => isAnalysisSupported(doc.file_type));
    const unsupportedDocs = documents.filter(doc => !isAnalysisSupported(doc.file_type));

    if (supportedDocs.length === 0) {
      toast({
        title: currentLanguage === 'da' ? "Fejl" : "Error",
        description: currentLanguage === 'da'
          ? "Ingen af de valgte dokumenter er i et understøttet format. Konvertér venligst til PDF eller brug et understøttet billedformat."
          : "None of the selected documents are in a supported format. Please convert to PDF or use a supported image format.",
        variant: "destructive",
      });
      return;
    }

    try {
      const documentFiles = await Promise.all(
        documents.map(async (doc) => {
          try {
            // Skip unsupported documents
            if (!isAnalysisSupported(doc.file_type)) {
              return {
                fileName: doc.file_name,
                fileType: doc.file_type,
                fileSize: doc.file_size,
                fileData: null,
                error: `Unsupported file format: ${doc.file_type}`
              };
            }
            
            const { data, error } = await supabase.storage
              .from('tender_documents')
              .download(doc.file_path);

            if (error) {
              console.error(`Error downloading ${doc.file_name}:`, error);
              return {
                fileName: doc.file_name,
                fileType: doc.file_type,
                fileSize: doc.file_size,
                fileData: null,
                error: `Failed to download: ${error.message}`
              };
            }

            return {
              fileName: doc.file_name,
              fileType: doc.file_type,
              fileSize: doc.file_size,
              fileData: data,
              error: null
            };
          } catch (err: any) {
            console.error(`Error processing ${doc.file_name}:`, err);
            return {
              fileName: doc.file_name,
              fileType: doc.file_type,
              fileSize: doc.file_size,
              fileData: null,
              error: `Failed to process: ${err.message}`
            };
          }
        })
      );

      const validDocuments = documentFiles.filter(doc => doc.fileData);
      const failedDocuments = documentFiles.filter(doc => doc.error);

      if (validDocuments.length === 0) {
        toast({
          title: currentLanguage === 'da' ? "Fejl" : "Error",
          description: currentLanguage === 'da' 
            ? "Kunne ikke behandle nogen af de valgte dokumenter" 
            : "Could not process any of the selected documents",
          variant: "destructive",
        });
        return;
      }

      const fileDataArray = await Promise.all(validDocuments.map(async doc => {
        if (!doc.fileData) return null;
        
        const arrayBuffer = await doc.fileData.arrayBuffer();
        
        const { data: processedData, mimeType: processedMimeType } = 
          await prepareDocumentForAI(arrayBuffer, doc.fileType);
        
        console.log(`Processed ${doc.fileName}: Original type: ${doc.fileType}, Processed type: ${processedMimeType}`);
        
        return {
          data: processedData,
          mimeType: processedMimeType
        };
      }));

      const validFileDataArray = fileDataArray.filter(item => item !== null) as Array<{
        data: string;
        mimeType: string;
      }>;

      // Create localized prompt based on current language
      const prompt = currentLanguage === 'da' 
        ? `Analysér følgende udbudsdokumenter:
${validDocuments.map(doc => `- ${doc.fileName} (${doc.fileType})`).join('\n')}

${failedDocuments.length > 0 ? 
`Bemærk: Følgende dokumenter kunne ikke behandles: 
${failedDocuments.map(doc => `- ${doc.fileName}: ${doc.error}`).join('\n')}` : ''}

Giv venligst en omfattende analyse for tilbudsgivere med følgende struktur:

1. **Projektoversigt**
   - Hvad udbydes?
   - Kort beskrivelse af projektets omfang

2. **Kvalifikationskrav**
   - Krav til juridisk status
   - Krav til økonomisk stabilitet
   - Erfaringskrav
   - Tekniske kapacitetskrav
   - Andre kvalifikationskrav

3. **Indleveringskrav**
   - Påkrævede dokumenter
   - Formatkrav
   - Antal kopier
   - Sprogkrav
   - Indleveringsmetode
   - Adresse for indlevering

4. **Tidsfrister**
   - Spørgsmålsfrist
   - Indleveringsfrist
   - Vedståelsesperiode

5. **Evalueringskriterier**
   - Tekniske evalueringskriterier og vægtning
   - Økonomiske evalueringskriterier og vægtning
   - Andre evalueringskriterier
   - Samlet vægtningssystem

6. **Tekniske specifikationer**
   - Centrale ydelseskrav
   - Overensstemmelseskrav
   - Garanti- og supportvilkår

7. **Potentielle opmærksomhedspunkter**
   - Eventuel uklar sprogbrug
   - Urealistiske tidsfrister
   - Byrdefulde vilkår og betingelser
   - Manglende information
   - Særlige klausuler, som tilbudsgivere bør være opmærksomme på

8. **Vigtige handlinger for tilbudsgivere**
   - Vigtige trin og anbefalinger

Formatér din analyse med klare overskrifter og punktopstillinger. Hvis information til et afsnit ikke er tilgængelig i dokumentet, angiv dette tydeligt.

VIGTIGT: 
- Start IKKE analysen med "Jeg vil analysere" eller lignende introduktioner.
- Afslut IKKE analysen med "Held og lykke" eller lignende fraser.
- Brug IKKE formuleringer som "baseret på de leverede dokumenter" eller andre meta-kommentarer.
- Sørg for KLAR formatering med god afstand mellem afsnit og punkter.
- Hold hvert punkt i listeform kort og præcist.`

        : `Analyze the following procurement documents:
${validDocuments.map(doc => `- ${doc.fileName} (${doc.fileType})`).join('\n')}

${failedDocuments.length > 0 ? 
`Note: The following documents could not be processed: 
${failedDocuments.map(doc => `- ${doc.fileName}: ${doc.error}`).join('\n')}` : ''}

Please provide a comprehensive analysis for bidders with the following structure:

1. **Project Overview**
   - What is being procured?
   - Brief description of the project scope

2. **Eligibility Criteria**
   - Legal status requirements
   - Financial stability requirements
   - Experience requirements
   - Technical capacity requirements
   - Other eligibility requirements

3. **Submission Requirements**
   - Required documents
   - Format requirements
   - Number of copies
   - Language requirements
   - Submission method
   - Address for submission

4. **Deadlines**
   - Clarification deadline
   - Submission deadline
   - Validity period

5. **Evaluation Criteria**
   - Technical evaluation criteria and weights
   - Financial evaluation criteria and weights
   - Other evaluation criteria
   - Overall weighting system

6. **Technical Specifications**
   - Key performance requirements
   - Compliance requirements
   - Warranty and support terms

7. **Potential Red Flags**
   - Any ambiguous language
   - Unrealistic deadlines
   - Onerous terms and conditions
   - Missing information
   - Special clauses bidders should pay attention to

8. **Key Actions for Bidders**
   - Important steps and recommendations

Format your analysis with clear headings and bullet points. If information for any section is not available in the document, please indicate this clearly.

IMPORTANT: 
- DO NOT start the analysis with "I will analyze" or similar introductions.
- DO NOT end the analysis with "Good luck" or similar phrases.
- DO NOT use phrases like "based on the provided documents" or other meta-commentary.
- Ensure CLEAR formatting with proper spacing between sections and bullet points.
- Keep each bullet point item short and concise.`;

      console.log(`Sending ${validFileDataArray.length} documents to Gemini API`);
      console.log(`Document types: ${validFileDataArray.map(file => file.mimeType).join(', ')}`);

      const systemInstruction = currentLanguage === 'da'
        ? "Du er en specialiseret ekspert i analyse af udbudsdokumenter. Gennemgå dokumenterne for at give detaljerede indsigter om udbudsprocessen. Formatér din respons med klare overskrifter og sektioner, fokusér på kritisk information for tilbudsgivere. Vær grundig men præcis. Hvis tekstudtrækningen fra dokumenterne virker ufuldstændig eller indeholder artefakter, skal du anerkende dette i din analyse. DU MÅ IKKE STARTE DIN RESPONS MED 'JEG VIL ANALYSERE' ELLER LIGNENDE FRASER. DU MÅ IKKE AFSLUTTE MED 'HELD OG LYKKE' ELLER LIGNENDE FRASER. GIV IKKE META-KOMMENTARER OM HVAD DU FÅR IND AF DATA."
        : "You are a specialized procurement document analysis expert. Review the documents to provide detailed insights about the procurement process. Format your response in clear markdown headings and sections, focusing on critical information for bidders. Be thorough but concise. If the text extraction from documents appears incomplete or contains artifacts, acknowledge this in your analysis. DO NOT START YOUR RESPONSE WITH 'I WILL ANALYZE' OR SIMILAR PHRASES. DO NOT END WITH 'GOOD LUCK' OR SIMILAR PHRASES. DO NOT PROVIDE META-COMMENTARY ABOUT WHAT DATA YOU'RE RECEIVING.";

      const response = await generateContent(prompt, {
        systemInstruction,
        temperature: 0.2, // Lower temperature for more focused analysis
        maxTokens: 4000, // Increase token limit for comprehensive analysis
        fileData: validFileDataArray, // Pass the processed file data array
      });

      if (response && response.generatedText) {
        setAnalysisResults(response.generatedText);
      } else {
        toast({
          title: currentLanguage === 'da' ? "Analyse mislykkedes" : "Analysis failed",
          description: currentLanguage === 'da' 
            ? "Kunne ikke generere analyse for de valgte dokumenter" 
            : "Could not generate analysis for the selected documents",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error analyzing documents:", error);
      toast({
        title: currentLanguage === 'da' ? "Analysefejl" : "Analysis error",
        description: currentLanguage === 'da' 
          ? "Der opstod en fejl under analysen af dokumenterne" 
          : "An error occurred while analyzing the documents",
        variant: "destructive",
      });
    }
  };

  // New function to save analysis as text
  const saveAnalysisAsText = () => {
    if (!analysisResults) return;
    
    // Create a clean version without markdown syntax
    const cleanText = analysisResults
      .replace(/## /g, '') // Remove markdown headers
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/- /g, '• '); // Replace markdown lists with bullet points
    
    // Create a blob with the analysis text
    const blob = new Blob([cleanText], { type: 'text/plain;charset=utf-8' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    
    // Create a filename based on the tender and documents
    const fileName = `tender_documents_analysis_${tenderId.substring(0, 8)}.txt`;
    a.download = fileName;
    
    // Trigger the download
    window.document.body.appendChild(a);
    a.click();
    
    // Clean up
    URL.revokeObjectURL(url);
    window.document.body.removeChild(a);
    
    toast({
      title: currentLanguage === 'da' ? 'Analysen er gemt' : 'Analysis saved',
      description: currentLanguage === 'da' 
        ? `Analysen er blevet gemt som ${fileName}` 
        : `Analysis has been saved as ${fileName}`,
    });
  };

  // Automatically start analysis when the dialog is opened with valid documents
  useEffect(() => {
    if (isOpen && documents.length > 0) {
      // Check if there are any supported documents
      const supportedDocs = documents.filter(doc => isAnalysisSupported(doc.file_type));
      
      if (supportedDocs.length > 0) {
        analyzeDocuments();
      }
    }
  }, [isOpen, documents]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("documents.analyzeSelectedDocuments")} ({documents.length})
          </DialogTitle>
          <DialogDescription>
            {currentLanguage === 'da' ? "Analyse af udvalgte udbudsdokumenter" : "Analysis of selected procurement documents"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">
                {currentLanguage === 'da' ? "Analyserer dokumenter..." : "Analyzing documents..."}
              </p>
            </div>
          ) : analysisResults ? (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <div
                className="markdown-content"
                dangerouslySetInnerHTML={{ __html: formatMarkdown(analysisResults) }}
              />
            </div>
          ) : (
            <p>{currentLanguage === 'da' ? "Ingen analyse tilgængelig endnu." : "No analysis available yet."}</p>
          )}
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            {t("common.close") || (currentLanguage === 'da' ? "Luk" : "Close")}
          </Button>
          {analysisResults && (
            <Button 
              onClick={saveAnalysisAsText}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {currentLanguage === 'da' ? 'Gem som tekst' : 'Save as text'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced formatMarkdown function for better spacing and structure
const formatMarkdown = (markdown: string): string => {
  if (!markdown) return '';
  
  return markdown
    // Headers with proper spacing
    .replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold mb-4 mt-6">$1</h1>')
    .replace(/^## (.*?)$/gm, '<h2 class="text-xl font-semibold my-4">$1</h2>')
    .replace(/^### (.*?)$/gm, '<h3 class="text-lg font-medium my-3">$1</h3>')
    
    // Text formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Lists with proper spacing
    .replace(/^- (.*?)$/gm, '<li class="ml-4 mb-2">$1</li>')
    .replace(/^(\d+)\. (.*?)$/gm, '<li class="ml-4 mb-2"><span class="font-semibold">$1.</span> $2</li>')
    
    // Group list items
    .replace(/<\/li>\n<li/g, '</li><li')
    
    // Blockquotes
    .replace(/^> (.*?)$/gm, '<blockquote class="pl-4 border-l-4 border-gray-300 italic my-3">$1</blockquote>')
    
    // Code blocks
    .replace(/```(.*?)```/gs, '<pre class="bg-muted p-4 rounded-md overflow-x-auto my-4"><code>$1</code></pre>')
    
    // Add paragraph spacing
    .replace(/\n\n/g, '<p class="my-3"></p>')
    
    // Additional spacing and structure for sections
    .replace(/<h2/g, '<div class="mt-6"></div><h2')
    .replace(/<\/h2>/g, '</h2><div class="mb-2"></div>')
    
    // Ensure list groups have proper markup
    .replace(/<li class="ml-4 mb-2">/g, '<ul class="list-disc space-y-2 my-3"><li class="ml-4 mb-2">')
    .replace(/<\/li><li class="ml-4 mb-2">/g, '</li><li class="ml-4 mb-2">')
    .replace(/<\/li>\s*(?!<li|<\/ul>)/g, '</li></ul>')
    
    // Fix any double-nested list issues
    .replace(/<ul class="list-disc space-y-2 my-3"><li class="ml-4 mb-2"><ul class="list-disc space-y-2 my-3">/g, 
            '<ul class="list-disc space-y-2 my-3"><li class="ml-4 mb-2">')
    .replace(/<\/ul><\/li><\/ul>/g, '</li></ul>')
    
    // Clean up any empty paragraphs
    .replace(/<p class="my-3"><\/p><p class="my-3"><\/p>/g, '<p class="my-3"></p>')
    .replace(/<\/h2><div class="mb-2"><\/div><p class="my-3"><\/p>/g, '</h2><div class="mb-2"></div>');
};
